import { useState } from 'react'
import { EmptyCell, type IColumn, Typography, Tooltip, Icon } from 'shared/ui'
import { DayjsFormats } from 'shared/lib'
import { uiStore } from 'shared/store/uiStore'
import { copyClientSecret } from 'pages/settings/pages/developer/pages/applications/lib/copyClientSecret'
import { type AuthClientRow } from 'pages/settings/pages/developer/pages/applications/model/AuthClientRow'
import styles from './styles.module.scss'

const COPIED_RESET_MS = 2000

const ClientSecretCell = ({ row }: { row: AuthClientRow }) => {
  const [copied, setCopied] = useState(false)

  const handleCopy = async () => {
    const success = await copyClientSecret(row.secret)
    if (success) {
      setCopied(true)
      setTimeout(() => setCopied(false), COPIED_RESET_MS)
    }
  }

  return (
    <Tooltip
      placement='top'
      label={
        <span className={styles.tooltipCopyLabel} onClick={handleCopy}>
          <Icon icon='copy' fontSize={14} />
          {copied ? 'Copied' : 'Copy'}
        </span>
      }
    >
      <Typography
        variant='body-md-regular'
        ariaLabel='AuthClientSecret'
        tag='div'
        ellipsis
        onClick={handleCopy}
        className={styles.secretCell}
      >
        {row.secret}
      </Typography>
    </Tooltip>
  )
}

export const applicationsTableColumns: IColumn<AuthClientRow>[] = [
  {
    field: 'id',
    name: 'Client ID',
    width: '80px',
    paddingHeader: '0 8px 0 12px',
    renderRowCell: (row) => (
      <Typography variant='body-md-regular' ariaLabel='AuthClientId' tag='div' ellipsis>
        {String(row.id)}
      </Typography>
    ),
  },
  {
    field: 'name',
    name: 'Name',
    width: '120px',
    maxWidth: '120px',
    minWidth: '120px',
    renderRowCell: (row) => (
      <Typography
        variant='body-md-regular'
        ariaLabel='AuthClientName'
        tag='div'
        ellipsis
        className={styles.nameCell}
        tooltipProps={{ label: row.name }}
      >
        {row.name || <EmptyCell />}
      </Typography>
    ),
  },
  {
    field: 'secret',
    name: 'Client secret',
    width: '360px',
    maxWidth: '360px',
    minWidth: '360px',
    renderRowCell: (row) => {
      if (!row.secret) return <EmptyCell />

      return <ClientSecretCell row={row} />
    },
  },
  {
    field: 'created_at',
    name: 'Created at',
    minWidth: 112,
    renderRowCell: (row) => {
      const createdAt = row.created_at && uiStore.dayjs(row.created_at).format(DayjsFormats.full6)

      if (!createdAt) return <EmptyCell />

      return (
        <Typography
          variant='body-md-regular'
          ariaLabel='AuthClientCreatedAt'
          tag='div'
          ellipsis
          tooltipProps={{ label: createdAt }}
        >
          {createdAt}
        </Typography>
      )
    },
  },
]
